import { type KeyboardEvent, type ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { filterCommands, type SearchCategory } from '../../domain/searchResult';
import { useGlobalSearchResults } from '../../application/globalSearch.query';

/**
 * A static, client-side command fed in by the app shell: a navigable page or a quick action.
 * Carries its own icon and either a route (`to`) or an imperative `run` (quick actions that
 * open a drawer/sheet). The shell owns these because only it knows how to open its surfaces.
 */
export interface UICommand {
  id: string;
  category: 'pages' | 'actions';
  title: string;
  subtitle?: string;
  /** Extra words to match against beyond the title (synonyms). */
  keywords?: string;
  icon: ReactNode;
  to?: string;
  run?: () => void;
}

/** A single rendered row — the unified shape both entity results and commands collapse into. */
export interface DisplayItem {
  id: string;
  category: SearchCategory;
  title: string;
  subtitle?: string;
  meta?: string;
  /** Command rows carry their own icon; entity rows leave this undefined and the panel supplies a category icon. */
  icon?: ReactNode;
  onSelect: () => void;
}

export interface DisplayGroup {
  category: SearchCategory;
  items: DisplayItem[];
}

export interface GlobalSearchController {
  term: string;
  setTerm: (term: string) => void;
  groups: DisplayGroup[];
  flat: DisplayItem[];
  activeId: string | null;
  setActiveId: (id: string) => void;
  isFetching: boolean;
  hasQuery: boolean;
  showEmpty: boolean;
  handleKeyDown: (event: KeyboardEvent) => void;
}

/**
 * Global-search controller — turns the raw term into grouped, keyboard-navigable rows.
 *
 * It merges server/client entity results (from {@link useGlobalSearchResults}) with the static
 * navigation commands passed in by the shell, orders everything by {@link CATEGORY_ORDER}, and
 * owns keyboard navigation (↑/↓ to move, Enter to select, Esc to close). Selecting a row
 * navigates (or runs the command) and closes the palette.
 */
export function useGlobalSearchController(commands: UICommand[], onClose: () => void): GlobalSearchController {
  const [term, setTerm] = useState('');
  const [rawActiveId, setActiveId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { groups: entityGroups, isFetching } = useGlobalSearchResults(term);

  const select = useCallback(
    (item: { to?: string; run?: () => void }) => {
      if (item.run) item.run();
      else if (item.to) navigate(item.to);
      onClose();
    },
    [navigate, onClose],
  );

  const groups = useMemo<DisplayGroup[]>(() => {
    const entityDisplay: DisplayGroup[] = entityGroups.map((group) => ({
      category: group.category,
      items: group.items.map((result) => ({
        id: result.id,
        category: result.category,
        title: result.title,
        subtitle: result.subtitle,
        meta: result.meta,
        onSelect: () => select({ to: result.to }),
      })),
    }));

    const matchedCommands = filterCommands(commands, term);
    const commandGroups: DisplayGroup[] = (['pages', 'actions'] as const).map((category) => ({
      category,
      items: matchedCommands
        .filter((command) => command.category === category)
        .map((command) => ({
          id: command.id,
          category,
          title: command.title,
          subtitle: command.subtitle,
          icon: command.icon,
          onSelect: () => select({ to: command.to, run: command.run }),
        })),
    }));

    // Entity groups already arrive in CATEGORY_ORDER (patients → staff) from groupResults, and the
    // command groups (pages → actions) come last in that same order, so concatenation needs no re-sort.
    return [...entityDisplay, ...commandGroups].filter((group) => group.items.length > 0);
  }, [entityGroups, commands, term, select]);

  const flat = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  // The highlighted row, derived from the raw selection — falls back to the first result whenever the
  // current highlight is no longer present (e.g. after the term changes), so no state-sync effect is needed.
  const activeId = useMemo(
    () => (rawActiveId && flat.some((item) => item.id === rawActiveId) ? rawActiveId : flat[0]?.id ?? null),
    [rawActiveId, flat],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (flat.length === 0) return;
      const index = flat.findIndex((item) => item.id === activeId);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const next = index < 0 ? 0 : (index + 1) % flat.length;
        setActiveId(flat[next].id);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = index <= 0 ? flat.length - 1 : index - 1;
        setActiveId(flat[prev].id);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const target = flat.find((item) => item.id === activeId) ?? flat[0];
        target?.onSelect();
      }
    },
    [flat, activeId, onClose],
  );

  const trimmed = term.trim();
  const hasQuery = trimmed.length > 0;
  const showEmpty = hasQuery && !isFetching && flat.length === 0;

  return {
    term,
    setTerm,
    groups,
    flat,
    activeId,
    setActiveId,
    isFetching,
    hasQuery,
    showEmpty,
    handleKeyDown,
  };
}
